import React, { useEffect, useReducer, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import reducer, { initialState } from 'reducers/brandingsSettingsReducer';

import SuccessToast from 'components/application/SuccessToast';
import Tabs from 'components/application/Tabs';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import DownloadLandingPreview from 'components/settings/projectConfiguration/branding/DownloadLandingPreview';
import EmailPreview from 'components/settings/projectConfiguration/branding/EmailPreview';
import Form from 'components/settings/projectConfiguration/branding/Form';

import {
  editableBrandingAsValues,
  getLogoState,
  persistBranding,
} from 'components/helpers/resources/brandings';

export default function BrandingSection({ isPoweredByEditable }) {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();

  const [brandingsSettings, dispatch] = useReducer(reducer, initialState);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.projectConfiguration.BrandingSection',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const handleBrandingFetch = () => {
    axios
      .get('/branding')
      .then((response) => {
        const branding = response.data.data;
        const logo = response.data.included.find(
          (inclusion) => inclusion.type === 'image',
        );
        dispatch({
          type: 'FETCHED_BRANDING',
          payload: { branding, logo },
        });
      })
      .catch(breadBoard.addInedibleToast);
  };

  useEffect(() => {
    handleBrandingFetch();
  }, []);

  const [activeTab, setActiveTab] = useState('downloading');

  // short-circuit if still pending
  if (brandingsSettings.application.pending.brandingFetch) return null;

  // derived values
  const isPersisted = !!brandingsSettings.domain.branding.id;
  const { editableBranding } = brandingsSettings.ui;
  const editableBrandingValues = editableBrandingAsValues(editableBranding);
  const logoState = getLogoState(
    brandingsSettings.domain.logo,
    brandingsSettings.ui.logo,
  );
  const logoSrc =
    !brandingsSettings.ui.logo.markedForDeletion &&
    brandingsSettings.ui.logo.src;
  const isEditingDisabled =
    brandingsSettings.application.pending.brandingPersisting;
  const { isCustomBranding } = brandingsSettings.application;

  // handlers
  const handleBrandingPersistanceRequest = (persistanceStrategy) => {
    dispatch({ type: 'REQUESTED_BRANDING_PERSISTANCE' });
    persistBranding(persistanceStrategy, editableBranding, {
      state: logoState,
      data: brandingsSettings.ui.logo.data,
    })
      .then((response) => {
        dispatch({
          type: 'PERSISTED_BRANDING',
          payload: {
            branding: response.data.data,
            logo: response.data.included.find(
              (inclusion) => inclusion.type === 'image',
            ),
          },
        });
      })
      .then(
        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey='settings.projectConfiguration.BrandingSection.handleBrandingPersistanceRequest.success'
                ns='components'
              />
            }
          />,
        ),
      )
      .catch(breadBoard.addInedibleToast);
  };
  const handleCreate = () => handleBrandingPersistanceRequest('create');
  const handleUpdate = () => handleBrandingPersistanceRequest('update');
  const handleUpload = (fileData) =>
    dispatch({
      type: 'SELECTED_LOGO',
      payload: { fileData, src: URL.createObjectURL(fileData) },
    });
  const handleColourToggle = (attributeName) =>
    dispatch({ type: 'TOGGLED_COLOUR', payload: { attribute: attributeName } });
  const handleColourChange = ({ attributeName, colour }) =>
    dispatch({
      type: 'CHANGED_COLOUR',
      payload: { value: colour, attribute: attributeName },
    });
  const handleLabelToggle = () => dispatch({ type: 'TOGGLED_LABEL' });
  const handleRemoveLogoClick = () => {
    dispatch({ type: 'LOGO_MARKED_FOR_DELETION' });
  };
  const createOrUpdate = () => (isPersisted ? handleUpdate() : handleCreate());
  const handleSaveClick = () =>
    isCustomBranding && !brandingsSettings.ui.logo.src ?
      dispatch({ type: 'INVALID_LOGO' })
    : createOrUpdate();
  const resetDefaults = () => dispatch({ type: 'RESET_DEFAULTS' });

  return (
    <>
      <h3 className='m-t-0 m-b-24 tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-900'>
        {i18nT('title', {
          interpolation: { escapeValue: false },
          singularRAMSName: currentActor.division.attributes.singularRAMSName,
        })}
      </h3>
      <div className='tw-flex tw-flex-row'>
        <div className='tw-flex-2 tw-pr-tw-flex tw-flex-1 tw-flex-col tw-pr-6'>
          <div className='svg-preview m-b-16 tw-bg-grey-025'>
            <Tabs
              activeTab={activeTab}
              modifiers={['centered']}
              onTabClick={setActiveTab}
            >
              <DownloadLandingPreview
                id='downloading'
                label={i18nTShared('downloading')}
                logoSrc={logoSrc}
                {...editableBrandingValues}
              />
              <EmailPreview
                id='emails'
                label={i18nTShared('emails')}
                logoSrc={logoSrc}
                {...editableBrandingValues}
              />
            </Tabs>
          </div>
        </div>
        <div className='tw-flex tw-flex-1 tw-flex-col'>
          <Form
            branding={editableBranding}
            disabled={isEditingDisabled}
            invalidLogo={brandingsSettings.ui.logo.invalid}
            isCustomBranding={isCustomBranding}
            isPoweredByEditable={isPoweredByEditable}
            logoSrc={logoSrc}
            onColourChange={handleColourChange}
            onColourToggle={handleColourToggle}
            onLabelToggle={handleLabelToggle}
            onRemoveLogoClick={handleRemoveLogoClick}
            onSaveClick={handleSaveClick}
            onUpload={handleUpload}
            resetDefaults={resetDefaults}
          />
        </div>
      </div>
    </>
  );
}
